import type { ReactNode, JSX} from "react";
import { cn } from "./cn";
import { Row } from "./Row";
import { Stack } from "./Stack";
import { Text } from "./Text";

export type EntityCardAccent = "cyan" | "amber" | "green" | "blue" | "purple" | "red" | "orange";

export interface EntityCardProps {
  /** Leading element (icon, date badge, avatar) */
  readonly leading?: ReactNode;
  /** Primary text */
  readonly title: string;
  /** Secondary text or rich content (subtitle, status line) */
  readonly subtitle?: ReactNode;
  /** Trailing element (date badge, action buttons) */
  readonly trailing?: ReactNode;
  /** Left border accent color */
  readonly accent?: EntityCardAccent;
  /** Click handler */
  readonly onClick?: () => void;
  /** Extra className */
  readonly className?: string;
}

const accentMap: Readonly<Record<EntityCardAccent, string>> = {
  cyan: "border-l-[#22d3ee]",
  amber: "border-l-amber",
  green: "border-l-green",
  blue: "border-l-blue",
  purple: "border-l-purple",
  red: "border-l-red",
  orange: "border-l-accent",
};

/**
 * A card for items within an EntitySection.
 * Shows leading element, title/subtitle, and trailing actions.
 * Optional colored left border accent.
 *
 * Generic — used for meeting cards, task cards, trigger cards, etc.
 */
export function EntityCard({
  leading,
  title,
  subtitle,
  trailing,
  accent,
  onClick,
  className,
}: EntityCardProps): JSX.Element {
  const Component = onClick ? "button" : "div";
  return (
    <Component
      type={onClick ? "button" : undefined}
      className={cn(
        "flex items-center gap-3 bg-surface-secondary rounded-xl px-4 py-3",
        "border border-edge",
        accent && `border-l-[3px] ${accentMap[accent]}`,
        onClick && "cursor-pointer hover:bg-surface-hover transition-colors text-left w-full",
        className,
      )}
      onClick={onClick}
    >
      {leading ? <div className="shrink-0">{leading}</div> : null}

      <Stack gap={0.5} flex1 truncate>
        <Text variant="body" weight="medium" truncate>{title}</Text>
        {subtitle ? (
          typeof subtitle === "string" ? (
            <Text variant="caption" color="tertiary" truncate>{subtitle}</Text>
          ) : (
            <div className="truncate">{subtitle}</div>
          )
        ) : null}
      </Stack>

      {trailing ? (
        <Row gap={1.5} align="center" className="shrink-0">
          {trailing}
        </Row>
      ) : null}
    </Component>
  );
}
